import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { faArrowTrendUp } from "@fortawesome/free-solid-svg-icons/faArrowTrendUp";
import { faArrowTrendDown } from "@fortawesome/free-solid-svg-icons/faArrowTrendDown";
import { faMinus } from "@fortawesome/free-solid-svg-icons/faMinus";

import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Tooltip } from "../../../utils/local-antd";
import { formatReportPercent } from "../../../utils/Reports";

const Row = styled(Flex)`
  align-items: center;
  gap: 0.25rem;
`;

type Props = {
  current: number;
  previous: number;
  /**
   * For figures where going up is the bad outcome — deals lost, junk entries —
   * so the colour tracks whether the news is good, not whether the arrow is up.
   */
  higherIsWorse?: boolean;
};

/**
 * A count on its own says little: "11 tasks" only means something next to what
 * the previous window did.
 */
export const ReportDelta: React.FC<Props> = ({
  current,
  previous,
  higherIsWorse,
}) => {
  const { t } = useTranslation();

  const diff = current - previous;

  // Going from nothing to something is not "infinity percent"; the absolute
  // change is the only honest thing to show.
  const percentFraction = previous ? diff / previous : null;

  const good = higherIsWorse ? diff < 0 : diff > 0;
  const color = diff === 0 ? "darkerSub" : good ? "success" : "danger";
  const icon =
    diff === 0 ? faMinus : diff > 0 ? faArrowTrendUp : faArrowTrendDown;

  return (
    <Tooltip title={t("reports.metric.vsPrevious", { value: previous })}>
      <Row>
        <AtiraIcon icon={icon} color={color} />
        <Text fontSize="s" color={color}>
          {percentFraction === null
            ? `${diff > 0 ? "+" : ""}${diff}`
            : `${percentFraction > 0 ? "+" : ""}${formatReportPercent(percentFraction)}%`}
        </Text>
      </Row>
    </Tooltip>
  );
};
